/**
 * The Estimates tab — a Resource Plan's estimate, built from a manually-arranged outline of
 * Estimated Hours Lines.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHY LINES, AND WHY THIS IS NOT THE GRID
 * ──────────────────────────────────────────────────────────────────────────────
 * Planned hours are typed month-by-month into the grid. **Estimated** hours are what the
 * work was sized at before it was staffed. Those are two different numbers and confusing
 * them is the thing to get right, so the estimate is entered here — never inside the grid,
 * and a line NEVER writes grid hours, rates, or Plan Lines (see the header of
 * src/db/schema/estimated-hours-lines.ts).
 *
 * This replaces the Workstream-grain estimate that used to live directly on
 * `Workstream.estimatedHours` / `Plan.estimatedHours` (docs/specs/resource-plan.md §11,
 * revised 2026-09-29). A plan's estimate is now a small tree (max depth 3) the planner
 * builds by hand — add a line, nest it under another, reorder it, exclude it, type its
 * hours. `plan.estimatedHours` and each Workstream's `estimatedHours` are a
 * server-maintained rollup of that tree (src/lib/estimated-hours-lines.ts) — they are
 * read-only here, and the whole tree is reference only: excluded lines stay visible so the
 * planner can see what was sized out, but they never subtract from anything but their own
 * total.
 *
 * Import stays backlog (§11) — there is no affordance for it here at all now, disabled or
 * otherwise, since the manual outline is the way in until it lands.
 */

import {
  ArrowDown,
  ArrowUp,
  CornerDownRight,
  Indent,
  MoreHorizontal,
  Outdent,
  Plus,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState as useReactState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { EstimatedHoursLineStructureEntry } from "@/hooks/use-estimated-hours-lines";
import {
  useCreateEstimatedHoursLine,
  useDeleteEstimatedHoursLine,
  useRestructureEstimatedHoursLines,
  useUpdateEstimatedHoursLine,
} from "@/hooks/use-estimated-hours-lines";
import {
  indent as indentLine,
  MAX_LINE_DEPTH,
  moveDown as moveLineDown,
  moveUp as moveLineUp,
  outdent as outdentLine,
} from "@/lib/estimated-hours-lines";
import { canEditPlanContent } from "@/lib/plan-version";
import { cn } from "@/lib/utils";
import type { EstimatedHoursLineView } from "@/schemas/estimated-hours-lines";
import type { ResourcePlanDetailView, WorkstreamView } from "@/schemas/resource-plans";

const DECIMAL2 = /^\d+(\.\d{1,2})?$/;
const NO_WORKSTREAM = "__none__";

export function fmtHours(value: string | null): string {
  if (value === null) return "—";
  const negative = value.startsWith("-");
  const [whole = "0"] = value.replace("-", "").split(".");
  return `${negative ? "−" : ""}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
}

/** A line effectively counts only if it and every ancestor is included. Cycle-safe. */
function computeEffectivelyExcluded(lines: readonly EstimatedHoursLineView[]): Set<string> {
  const byId = new Map(lines.map((l) => [l.id, l] as const));
  const memo = new Map<string, boolean>();
  const visiting = new Set<string>();

  function resolve(line: EstimatedHoursLineView): boolean {
    const cached = memo.get(line.id);
    if (cached !== undefined) return cached;
    if (visiting.has(line.id)) return false;
    visiting.add(line.id);
    const parent = line.parentLineId !== null ? byId.get(line.parentLineId) : undefined;
    const excluded = !line.included || (parent !== undefined && resolve(parent));
    visiting.delete(line.id);
    memo.set(line.id, excluded);
    return excluded;
  }

  const out = new Set<string>();
  for (const line of lines) {
    if (resolve(line)) out.add(line.id);
  }
  return out;
}

function countDescendants(childrenOf: Map<string, EstimatedHoursLineView[]>, id: string): number {
  const kids = childrenOf.get(id) ?? [];
  return kids.reduce((sum, kid) => sum + 1 + countDescendants(childrenOf, kid.id), 0);
}

export function EstimatesPanel({ plan }: { plan: ResourcePlanDetailView }) {
  const createLine = useCreateEstimatedHoursLine(plan.id);
  const updateLine = useUpdateEstimatedHoursLine();
  const deleteLine = useDeleteEstimatedHoursLine();
  const restructure = useRestructureEstimatedHoursLines(plan.id);

  const lines = plan.estimatedHoursLines;
  const workstreams = plan.workstreams.filter(
    (w): w is WorkstreamView & { id: string } => w.id !== null,
  );

  const structureLines = useMemo(
    () => lines.map((l) => ({ id: l.id, parentLineId: l.parentLineId, position: l.position })),
    [lines],
  );

  const childrenOf = useMemo(() => {
    const map = new Map<string, EstimatedHoursLineView[]>();
    for (const line of lines) {
      if (line.parentLineId === null) continue;
      const bucket = map.get(line.parentLineId) ?? [];
      bucket.push(line);
      map.set(line.parentLineId, bucket);
    }
    return map;
  }, [lines]);

  const excludedIds = useMemo(() => computeEffectivelyExcluded(lines), [lines]);

  // A quick second click computes its move/indent/outdent from whatever structure was last
  // fetched — so every outline-mutating control is disabled while any of the four line
  // mutations is in flight, rather than risk a stale restructure undoing (or 422-ing
  // against) the one still in progress. Inline name/hours inputs commit independent
  // PATCHes of a single field and stay enabled throughout.
  const busy =
    createLine.isPending || updateLine.isPending || deleteLine.isPending || restructure.isPending;

  function confirmReplaceHours(parent: EstimatedHoursLineView): boolean {
    return window.confirm(
      `"${parent.name}" has ${fmtHours(parent.hours)} hours. Giving it sub-lines replaces them with the sub-lines' total. Continue?`,
    );
  }

  function addLine() {
    createLine.mutate({ name: "New line" });
  }

  function addSubLine(parent: EstimatedHoursLineView) {
    if (parent.hours !== null && !confirmReplaceHours(parent)) return;
    createLine.mutate({ name: "New line", parentLineId: parent.id });
  }

  function onDelete(line: EstimatedHoursLineView) {
    const descendants = countDescendants(childrenOf, line.id);
    const message =
      descendants > 0
        ? `Delete "${line.name}"? Its ${descendants} sub-line${descendants === 1 ? "" : "s"} go with it.`
        : `Delete "${line.name}"?`;
    if (!window.confirm(message)) return;
    deleteLine.mutate(line.id);
  }

  /** A line's siblings, in display order — `lines` is already depth-first ordered so a
   * same-parent filter preserves position order without re-sorting. */
  function siblingsOf(line: EstimatedHoursLineView): EstimatedHoursLineView[] {
    return line.parentLineId === null
      ? lines.filter((l) => l.parentLineId === null)
      : (childrenOf.get(line.parentLineId) ?? []);
  }

  function onIndent(line: EstimatedHoursLineView, result: EstimatedHoursLineStructureEntry[]) {
    const siblings = siblingsOf(line);
    const index = siblings.findIndex((s) => s.id === line.id);
    const newParent = index > 0 ? siblings[index - 1] : undefined;
    if (newParent !== undefined && newParent.hours !== null && !confirmReplaceHours(newParent)) {
      return;
    }
    restructure.mutate(result);
  }

  return (
    <section className="rounded-md border border-border bg-card">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <h2 className="font-display text-sm font-semibold">Estimated hours</h2>
          <p className="mt-0.5 max-w-[52ch] text-sm leading-relaxed text-muted-foreground">
            What the work was sized at, before it was staffed. The plan is checked against it.
          </p>
        </div>
        {canEditPlanContent(plan) && (
          <Button size="sm" onClick={addLine} disabled={busy}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add line
          </Button>
        )}
      </header>

      {lines.length === 0 ? (
        <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
          <p className="max-w-[46ch] text-sm text-muted-foreground">
            No lines yet. Add the pieces of work this plan was sized from — the plan is checked
            against their total.
          </p>
          {canEditPlanContent(plan) && (
            <Button size="sm" variant="outline" onClick={addLine} disabled={busy}>
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add line
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* A floor, not a width: below it the table scrolls sideways rather than
              crushing the Line column down to a few characters. */}
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="w-8 px-2 py-2" />
                <th className="px-2 py-2 font-semibold">Line</th>
                <th className="w-44 px-2 py-2 font-semibold">Workstream</th>
                <th className="w-32 px-4 py-2 text-right font-semibold">Estimated</th>
                <th className="w-24 px-4 py-2 text-right font-semibold">Planned</th>
                <th className="w-40 px-4 py-2 text-right font-semibold">Reconciliation</th>
                <th className="w-10 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const isExcluded = excludedIds.has(line.id);
                const moveUpResult = moveLineUp(structureLines, line.id);
                const moveDownResult = moveLineDown(structureLines, line.id);
                const indentResult = indentLine(structureLines, line.id);
                const outdentResult = outdentLine(structureLines, line.id);
                const canAddSubLine = line.depth < MAX_LINE_DEPTH;

                return (
                  <tr
                    key={line.id}
                    className={cn("border-t border-border", isExcluded && "text-muted-foreground")}
                  >
                    <td className="px-2 py-1.5">
                      <Checkbox
                        checked={line.included}
                        disabled={!canEditPlanContent(plan) || busy}
                        aria-label={`Count ${line.name} toward the estimate`}
                        onCheckedChange={(checked) =>
                          updateLine.mutate({ id: line.id, included: checked === true })
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <div
                        className={cn(
                          "flex items-center",
                          line.depth === 2 && "pl-6",
                          line.depth === 3 && "pl-12",
                        )}
                      >
                        <NameInput
                          value={line.name}
                          disabled={!canEditPlanContent(plan)}
                          onCommit={(name) => updateLine.mutate({ id: line.id, name })}
                        />
                      </div>
                    </td>
                    <td className="px-2 py-1.5">
                      {line.depth === 1 ? (
                        <Select
                          value={line.workstreamId ?? NO_WORKSTREAM}
                          disabled={!canEditPlanContent(plan) || busy}
                          onValueChange={(value) =>
                            updateLine.mutate({
                              id: line.id,
                              workstreamId: value === NO_WORKSTREAM ? null : value,
                            })
                          }
                        >
                          <SelectTrigger size="sm" className="h-8 w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NO_WORKSTREAM}>No Workstream</SelectItem>
                            {workstreams.map((ws) => (
                              <SelectItem key={ws.id} value={ws.id}>
                                {ws.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : null}
                    </td>
                    <td className="px-4 py-1.5 text-right">
                      {line.hasChildren ? (
                        <span
                          className={cn(
                            "tabular-nums text-muted-foreground",
                            isExcluded && "line-through",
                          )}
                          title="Total of included sub-lines"
                        >
                          {fmtHours(line.effectiveHours)}
                        </span>
                      ) : (
                        <EstimateInput
                          value={line.hours ?? ""}
                          disabled={!canEditPlanContent(plan)}
                          ariaLabel={`Estimated hours for ${line.name}`}
                          className={isExcluded ? "line-through" : undefined}
                          onCommit={(next) =>
                            updateLine.mutate({ id: line.id, hours: next === "" ? null : next })
                          }
                        />
                      )}
                    </td>
                    <td className="px-4 py-1.5" />
                    <td className="px-4 py-1.5" />
                    <td className="px-2 py-1.5 text-right">
                      {canEditPlanContent(plan) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              disabled={busy}
                              aria-label={`Actions for ${line.name}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={!canAddSubLine || busy}
                              onSelect={() => addSubLine(line)}
                            >
                              <CornerDownRight className="mr-2 h-3.5 w-3.5" />
                              Add sub-line
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={moveUpResult === null || busy}
                              onSelect={() => {
                                if (moveUpResult !== null) restructure.mutate(moveUpResult);
                              }}
                            >
                              <ArrowUp className="mr-2 h-3.5 w-3.5" />
                              Move up
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={moveDownResult === null || busy}
                              onSelect={() => {
                                if (moveDownResult !== null) restructure.mutate(moveDownResult);
                              }}
                            >
                              <ArrowDown className="mr-2 h-3.5 w-3.5" />
                              Move down
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={indentResult === null || busy}
                              onSelect={() => {
                                if (indentResult !== null) onIndent(line, indentResult);
                              }}
                            >
                              <Indent className="mr-2 h-3.5 w-3.5" />
                              Indent
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={outdentResult === null || busy}
                              onSelect={() => {
                                if (outdentResult !== null) restructure.mutate(outdentResult);
                              }}
                            >
                              <Outdent className="mr-2 h-3.5 w-3.5" />
                              Outdent
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={busy}
                              className="text-destructive focus:text-destructive"
                              onSelect={() => onDelete(line)}
                            >
                              <Trash2 className="mr-2 h-3.5 w-3.5" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-border bg-muted/40 font-semibold">
              <tr>
                <td className="px-2 py-2" />
                <td className="px-2 py-2">Total</td>
                <td className="px-2 py-2" />
                <td className="px-4 py-2 text-right tabular-nums">
                  {fmtHours(plan.estimatedHours)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtHours(plan.totalHours)}</td>
                <td className="px-4 py-2 text-right">
                  <ReconciliationCell
                    band={plan.estimateBand}
                    percent={plan.estimateVariancePercent}
                    varianceHours={plan.estimateVarianceHours}
                  />
                </td>
                <td className="px-2 py-2" />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {workstreams.length > 0 && (
        <div className="border-t border-border px-4 py-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            By Workstream
          </h3>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="py-1.5 font-semibold">Workstream</th>
                  <th className="w-32 py-1.5 text-right font-semibold">Estimated</th>
                  <th className="w-28 py-1.5 text-right font-semibold">Planned</th>
                  <th className="w-40 py-1.5 text-right font-semibold">Reconciliation</th>
                </tr>
              </thead>
              <tbody>
                {workstreams.map((ws) => (
                  <tr key={ws.id} className="border-t border-border">
                    <td className="py-1.5">
                      <span className="font-medium">{ws.name}</span>
                      {ws.type !== null && (
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          {ws.type === "RunSupport" ? "Run / Support" : ws.type}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {fmtHours(ws.estimatedHours)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">{fmtHours(ws.totalHours)}</td>
                    <td className="py-1.5 text-right">
                      <ReconciliationCell
                        band={ws.estimateBand}
                        percent={ws.estimateVariancePercent}
                        varianceHours={ws.estimateVarianceHours}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            A Workstream&rsquo;s estimate is the total of the lines tagged to it.
          </p>
        </div>
      )}

      <footer className="border-t border-border px-4 py-3 text-xs leading-relaxed text-muted-foreground">
        <p>
          Lines are a reference only — they never change the grid. Excluded lines stay visible but
          don&rsquo;t count.
        </p>
      </footer>
    </section>
  );
}

/** Commits on blur — a mutation per keystroke would be absurd. */
function EstimateInput(props: {
  value: string;
  disabled: boolean;
  ariaLabel: string;
  className?: string;
  onCommit: (value: string) => void;
}) {
  const [local, setLocal] = useStateFromProp(props.value);
  return (
    <Input
      // A string, not type="number": these are exact decimals and a number input would
      // hand back a float64.
      inputMode="decimal"
      aria-label={props.ariaLabel}
      value={local}
      disabled={props.disabled}
      placeholder="—"
      onChange={(event) => {
        const next = event.target.value;
        if (next === "" || /^\d*\.?\d{0,2}$/.test(next)) setLocal(next);
      }}
      onBlur={() => {
        if (local === props.value) return;
        if (local !== "" && !DECIMAL2.test(local)) {
          setLocal(props.value);
          return;
        }
        props.onCommit(local);
      }}
      className={cn("h-8 w-28 text-right tabular-nums", props.className)}
    />
  );
}

/** A line's name, inline-editable. An empty commit reverts rather than clearing the name. */
function NameInput(props: { value: string; disabled: boolean; onCommit: (value: string) => void }) {
  const [local, setLocal] = useStateFromProp(props.value);
  return (
    <Input
      aria-label="Line name"
      value={local}
      disabled={props.disabled}
      onChange={(event) => setLocal(event.target.value)}
      onBlur={() => {
        const trimmed = local.trim();
        if (trimmed === "") {
          setLocal(props.value);
          return;
        }
        if (trimmed === props.value) return;
        setLocal(trimmed);
        props.onCommit(trimmed);
      }}
      className="h-8 w-full"
    />
  );
}

/** Local state that re-syncs when the server value changes underneath. */
function useStateFromProp(value: string): [string, (next: string) => void] {
  const [local, setLocal] = useReactState(value);
  useEffect(() => setLocal(value), [value]);
  return [local, setLocal];
}

export function ReconciliationCell(props: {
  band: string;
  percent: string | null;
  varianceHours: string | null;
}) {
  if (props.band === "no-estimate") {
    return <span className="text-sm text-muted-foreground/70">no estimate</span>;
  }
  const under = props.percent?.startsWith("-") === true;
  const magnitude = props.percent?.replace("-", "") ?? "0.00";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums",
        props.band === "reconciled" && "bg-success-100 text-success-700 dark:text-success-400",
        props.band === "warn" && "bg-warning-100 text-warning-700 dark:text-warning-400",
        props.band === "red" && "bg-error-100 text-error-700 dark:text-error-400",
      )}
      title={`${props.varianceHours ?? "0"} hours against the estimate`}
    >
      {props.band === "reconciled"
        ? "reconciled"
        : `${under ? "−" : "+"}${magnitude}% · ${fmtHours(props.varianceHours)} hrs`}
    </span>
  );
}
